import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetInstitutionalRulesetByIdQuery,
  useUpdateInstitutionalRulesetMutation,
} from "store/queries/institutionalRuleset";

import {
  IInstitutionalRulesetFormData,
  INITIAL_INSTITUTIONAL_FORM_DATA,
  InstitutionalRulesetForm,
} from "./components/InstitutionalRulesetForm";

export function EditInstitutionalRulesetPage() {
  useDocumentTitle("Edit Institutional Ruleset");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetInstitutionalRulesetByIdQuery(
    id ?? ""
  );
  const [updateMutation, { isLoading: isUpdating }] =
    useUpdateInstitutionalRulesetMutation();
  const [formData, setFormData] = useState<IInstitutionalRulesetFormData>(
    INITIAL_INSTITUTIONAL_FORM_DATA
  );

  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      providerId: ruleset.providerId,
      payerId: ruleset.payerId,
      providerIds: [],
      payerIds: [],
      primarySelectionMethod:
        ruleset.primarySelectionMethod ?? "CONTRACTED_DOLLARS",
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      await updateMutation({
        id,
        primarySelectionMethod: formData.primarySelectionMethod,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Institutional ruleset updated successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to update institutional ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <InstitutionalRulesetForm
        title="Edit Institutional Ruleset"
        submitLabel="Save Changes"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        scopeLocked
      />
      <RuleHistory histories={data?.data?.history} />
    </>
  );
}
